import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpenText, Plus } from "lucide-react";
import {
  Button,
  DashedArrow,
  EmptyState,
  Field,
  Page,
  Spinner,
  Table,
  VendorMark,
  type Column,
  type EmptyStateStep,
} from "../../components";
import { useConnections, useMigrations, useSystems } from "../../api/hooks";
import { MIGRATION_STATES, formatDate, type Migration, type MigrationState } from "../../api/types";
import { MigrationStateBadge, pauseResumeText } from "../migrationState";
import { formatRelativePast } from "../../i18n/relativeTime";
import { vendorName } from "../../i18n/oauthCopy";
import { t, tDynamic } from "../../i18n/t";

/**
 * §B6.5, redesigned as Main.html: the filters are three selects (state, source system, destination system —
 * D8, no text search, no summary tiles) on a sunken strip at the top of the card; the rows are a table; the
 * pager is cursor-only Previous/Next (no "1–8 of N").
 *
 * The blocking count is `open_blocking_reviews` on the row — §A9.7.1 computes it with **one grouped count query**
 * for the whole page, so this screen must not fetch review items per row. If the field is ever absent, the fix is
 * in the API, not a per-row query here.
 *
 * §A4.3: the filters are carried on every page and are not encoded in the cursor, so changing one starts again —
 * which is why `onFilterChange` clears the cursor stack rather than trying to keep a position.
 *
 * §P4: "no migrations at all" (unfiltered, first page, empty) is a different situation from "no migrations
 * match this filter" — the latter keeps the table's empty row, the former gets MigrationsEmpty's empty state:
 * icon tiles, the numbered step cards (their done/next state is real: it comes from `list_connections`, the
 * same usability rule `ConnectScreen.pick` applies), and the primary call to action.
 */
export function MigrationsList(): JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState("");
  const [sourceSystem, setSourceSystem] = useState("");
  const [destinationSystem, setDestinationSystem] = useState("");
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;
  const query = useMigrations({
    state,
    source_system: sourceSystem,
    destination_system: destinationSystem,
    cursor,
  });
  const systems = useSystems();

  const isFirstPage = cursors.length === 1;
  const unfiltered = state === "" && sourceSystem === "" && destinationSystem === "";
  const noMigrationsAtAll =
    !query.isPending && unfiltered && isFirstPage && (query.data?.items.length ?? 0) === 0;
  // Only the no-migrations-at-all state reads connections (for the step cards), so the list itself never
  // pays for the extra request.
  const connections = useConnections({ enabled: noMigrationsAtAll });

  const restart = (apply: () => void) => {
    apply();
    setCursors([null]);
  };

  if (noMigrationsAtAll) {
    return (
      <Page eyebrow={t("operator.eyebrow")} title={t("operator.title")}>
        <MigrationsEmpty connections={connections.data ?? []} onStart={() => navigate("/migrations/new")} />
      </Page>
    );
  }

  return (
    <Page
      eyebrow={t("operator.eyebrow")}
      title={t("operator.title")}
      caption={t("operator.caption")}
      actions={
        <Button variant="primary" onClick={() => navigate("/migrations/new")}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          {t("migrations.wizard.start_button")}
        </Button>
      }
    >
      <section className="card card--flush" aria-label={t("operator.caption")}>
        <div className="filters">
          <Field label={t("operator.filter_state")}>
            {({ id }) => (
              <select id={id} value={state} onChange={(event) => restart(() => setState(event.target.value))}>
                <option value="">{t("operator.filter_all")}</option>
                {MIGRATION_STATES.map((value) => (
                  <option key={value} value={value}>
                    {tDynamic("state.", value)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t("operator.filter_source")}>
            {({ id }) => (
              <select
                id={id}
                value={sourceSystem}
                onChange={(event) => restart(() => setSourceSystem(event.target.value))}
              >
                <option value="">{t("operator.filter_all_systems")}</option>
                {(systems.data?.sources ?? []).map((value) => (
                  <option key={value} value={value}>
                    {vendorName(value)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t("operator.filter_destination")}>
            {({ id }) => (
              <select
                id={id}
                value={destinationSystem}
                onChange={(event) => restart(() => setDestinationSystem(event.target.value))}
              >
                <option value="">{t("operator.filter_all_systems")}</option>
                {(systems.data?.destinations ?? []).map((value) => (
                  <option key={value} value={value}>
                    {vendorName(value)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </div>
        {query.isPending ? (
          <Spinner />
        ) : (
          <Table
            bare
            caption={t("operator.caption")}
            columns={COLUMNS}
            rows={query.data?.items ?? []}
            rowKey={(row) => row.id}
            emptyMessage={t("operator.empty")}
            rowClassName={(row) => (row.state === "failed" ? "is-fail" : undefined)}
          />
        )}
        <div className="table-pager">
          <Button
            size="compact"
            disabled={cursors.length === 1}
            onClick={() => setCursors((prev) => prev.slice(0, -1))}
          >
            {t("operator.prev_page")}
          </Button>
          <Button
            size="compact"
            disabled={(query.data?.next_cursor ?? null) === null}
            onClick={() => setCursors((prev) => [...prev, query.data?.next_cursor ?? null])}
          >
            {t("operator.next_page")}
          </Button>
        </div>
      </section>
    </Page>
  );
}

const COLUMNS: readonly Column<Migration>[] = [
  {
    key: "id",
    header: t("operator.col_migration"),
    render: (row) => (
      // §8: the first 8 characters in Geist Mono; the full id in `title` and in the link's aria-label.
      <Link
        className="mono miglink"
        to={`/migrations/${row.id}`}
        title={row.id}
        aria-label={t("operator.open", { id: row.id })}
      >
        {row.id.slice(0, 8)}
      </Link>
    ),
  },
  {
    key: "route",
    header: t("operator.col_route"),
    render: (row) => (
      <span className="cellvendor cellvendor--route">
        <VendorMark system={row.source_system} size={22} />
        {vendorName(row.source_system)}
        <ArrowRight
          size={14}
          strokeWidth={2}
          role="img"
          aria-label={t("operator.route_to")}
          className="cellvendor__arrow"
        />
        <VendorMark system={row.destination_system} size={22} />
        {vendorName(row.destination_system)}
      </span>
    ),
  },
  {
    key: "state",
    header: t("operator.col_state"),
    render: (row) => (
      <span className="statecell">
        <MigrationStateBadge state={row.state as MigrationState} pause={row.pause} />
        {row.pause === null || row.pause === undefined ? null : (
          <span className="muted statecell__note" data-testid={`row-pause-${row.id}`}>
            {pauseResumeText(row.pause)}
          </span>
        )}
      </span>
    ),
  },
  {
    key: "conversion",
    header: t("operator.col_conversion"),
    render: (row) => <span className="mono">{formatDate(row.conversion_date) || "—"}</span>,
  },
  {
    key: "attempt",
    header: t("operator.col_attempt"),
    render: (row) => <span className="mono">{row.load_attempt ?? "—"}</span>,
  },
  {
    key: "blocking",
    header: t("operator.col_blocking"),
    render: (row) => {
      const count = row.open_blocking_reviews ?? 0;
      return <span className={count > 0 ? "mono numfail" : "mono muted"}>{count}</span>;
    },
  },
  {
    key: "updated",
    header: t("operator.col_updated"),
    render: (row) => (
      <span className="muted" data-live>
        {formatRelativePast(row.updated_at)}
      </span>
    ),
  },
];

/**
 * A connection is a usable endpoint under the same rule `ConnectScreen.pick` enforces (and the server
 * double-checks at `create_migration`): not confirmed-revoked, and not awaiting a revoke retry.
 */
function usableConnections(connections: readonly { system: string; revoke_state?: string | null }[]) {
  return connections.filter(
    (connection) => connection.revoke_state !== "revoked" && connection.revoke_state !== "revoke_failed",
  );
}

function MigrationsEmpty({
  connections,
  onStart,
}: {
  connections: readonly { system: string; revoke_state?: string | null }[];
  onStart: () => void;
}): JSX.Element {
  const usable = usableConnections(connections);
  const sourceDone = usable.length >= 1;
  const destinationDone = usable.length >= 2;
  const steps: readonly EmptyStateStep[] = [
    {
      label: t("migrations.wizard.empty_step_source"),
      sub: sourceDone
        ? t("migrations.wizard.empty_step_connected", { vendor: vendorName(usable[0]?.system ?? "") })
        : t("migrations.wizard.empty_step_source_sub"),
      state: sourceDone ? "done" : "next",
    },
    {
      label: t("migrations.wizard.empty_step_destination"),
      sub: destinationDone
        ? t("migrations.wizard.empty_step_connected", { vendor: vendorName(usable[1]?.system ?? "") })
        : t("migrations.wizard.empty_step_destination_sub"),
      state: destinationDone ? "done" : sourceDone ? "next" : "todo",
    },
    {
      label: t("migrations.wizard.empty_step_create"),
      sub: t("migrations.wizard.empty_step_create_sub"),
      state: destinationDone ? "next" : "todo",
    },
  ];
  return (
    <EmptyState
      title={t("migrations.wizard.empty_title")}
      body={t("migrations.wizard.empty_body")}
      icon={
        <>
          <span className="empty__icon">
            <BookOpenText size={26} strokeWidth={2} />
          </span>
          <DashedArrow />
          <span className="empty__icon empty__icon--solid">
            <BookOpenText size={26} strokeWidth={2} />
          </span>
        </>
      }
      steps={steps}
      action={
        <Button variant="primary" onClick={onStart}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          {t("migrations.wizard.start_button")}
        </Button>
      }
    />
  );
}
